import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useI18n } from "../context/I18nContext";
import { IMAGES } from "../lib/images";
import { scrollToId } from "../lib/scroll";
import { EASE, MaskedLines } from "./Reveal";

export default function Hero() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex h-[100svh] min-h-[640px] items-end overflow-hidden bg-charcoal"
    >
      <motion.div className="absolute inset-0" style={{ y }}>
        <motion.img
          src={IMAGES.hero}
          alt={t.hero.imageAlt}
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.6, ease: EASE }}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-charcoal/30" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-9 md:px-10 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.8, ease: EASE }}
          className="mb-4 inline-flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-cream/70 md:mb-6 md:text-[11px] md:tracking-[0.35em]"
        >
          <span className="h-px w-10 bg-terra" />
          {t.hero.overline}
        </motion.p>

        <h1 className="font-serif text-[9vw] leading-[1.06] text-cream sm:text-6xl sm:leading-[1.02] lg:text-7xl xl:text-[5.5rem]">
          <MaskedLines lines={[t.hero.titleA, t.hero.titleB]} delay={0.35} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.9, ease: EASE }}
          className="mt-4 max-w-xl text-sm leading-relaxed text-cream/85 sm:mt-6 sm:text-base sm:text-lg"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
          className="mt-6 flex flex-col gap-3 sm:mt-9 sm:flex-row sm:items-center sm:gap-4"
        >
          <button
            data-testid="hero-cta-book"
            onClick={() => navigate("/reservas")}
            className="rounded-full bg-terra px-7 py-3 text-sm font-medium text-cream transition-all duration-300 hover:scale-[1.02] hover:bg-terra-dark sm:px-8 sm:py-4"
          >
            {t.hero.ctaPrimary}
          </button>
          <button
            data-testid="hero-cta-discover"
            onClick={() => scrollToId("concepto")}
            className="rounded-full border border-cream/40 px-7 py-3 text-sm font-medium text-cream transition-all duration-300 hover:border-cream hover:bg-cream/10 sm:px-8 sm:py-4"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-6 flex items-end justify-between sm:mt-10"
        >
          <p data-testid="hero-min-note" className="text-xs tracking-wide text-cream/55">
            {t.hero.note}
          </p>
          <div className="hidden items-center gap-3 md:flex">
            <span className="text-[10px] uppercase tracking-[0.3em] text-cream/45">{t.hero.scroll}</span>
            <span className="relative block h-12 w-px overflow-hidden bg-cream/20">
              <motion.span
                className="absolute left-0 top-0 h-4 w-px bg-terra"
                animate={{ y: [0, 48] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              />
            </span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
